import React, {
    useContext,
    useRef,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from "react";
import dynamic from "next/dynamic";
import { Bookmarks } from "@bloomberg/bookmarks";
import { ContentType } from "@bloomberg/bookmarks/dist/interfaces";
import {
    Assistance,
    Disclaimer,
    FloatingCopyLink,
    Footnotes,
    Trashline,
    Placeholder,
} from "@bloomberg/media-ui-article";
import { createClasses, TerminalTout } from "@bloomberg/media-ui-core";
import { TipKey, TipVariant, useTippy } from "@bloomberg/tippy";
import { useFortress } from "@foundation/fortress";
import { RichText } from "@foundation/rich-text-react-renderer";
import { ScreenContext, useScreen } from "@foundation/screen";
import {
    endStatsigPerfLog,
    startStatsigPerfLog,
    useLayer,
    useStatsigClient,
} from "@foundation/statsig";

import { WEEKEND_NICODES } from "~constants";
import { BlensWithBrokerAd } from "~components/Blens";
import BrandMetricsModule from "~components/BrandMetrics";
import ContactUs from "~components/ContactUs";
import Disqus from "~components/Disqus";
import Footer from "~components/Footer";
import { GridCenterContent } from "~components/GridLayout";
import LanguagePreference from "~components/LanguagePreference";
import BasicByline from "~components/Lede/BasicByline";
import MagazineInsert from "~components/MagazineInsert";
import Share from "~components/Share";
import ShareFeedback from "~components/ShareFeedback";
import StoryBody from "~components/StoryBody";
import SubscriberLabel from "~components/SubscriberLabel";
import Takeaways from "~components/Takeaways";
import SummaryOnlyTakeaways from "~components/Takeaways/variants/SummaryOnly/SummaryOnlyTakeaways";
import { TextToSpeech } from "~components/TextToSpeech";
import TipTout from "~components/TipTout";
import TranslationDisclaimer from "~components/TranslationDisclaimer";
import UpNext from "~components/UpNext";
import WeekendRedirectTout from "~components/WeekendRedirectTout";
import options, { footnotesOptions } from "~config/richtext";
import { getWeekendCollectionId, OPINION_ARTICLE_TYPE } from "~types/types";
import {
    getNumberOfParagraphs,
    hasInlineAudioPlayer,
} from "~utils/blockAndCharCountUtil";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import isEnabled from "~utils/features";
import { getFortressState } from "~utils/getFortressState";
import { getShouldShowAITakeaways } from "~utils/getShouldShowAITakeaways";
import { TranslatedMessage } from "~utils/i18n/translate";
import isOpinion from "~utils/isOpinion";
import { Tracker } from "~utils/tracker";

import styles from "./styles.module.css";

const IS_BLENS_ENABLED = isEnabled("enableBlens");
const IS_THE_WEEKEND_ENABLED = isEnabled("enableTheWeekend");

export const SURVEY_FEEDBACK_URL =
    "https://bmedia.iad1.qualtrics.com/jfe/form/SV_0xQ0jMsQ7QLRlj0";

const NEXT_PUBLIC_REGISTRATION_URL =
    process.env.NEXT_PUBLIC_REGISTRATION_URL || "";

const AuthorBioList = dynamic(
    () => import("~components/AuthorBioList").then((mod) => mod),
    {
        /* c8 ignore next */
        loading: () => (
            <p>
                <TranslatedMessage id="loading" defaultMessage="Loading..." />
            </p>
        ),
    }
);

const copyLinkTracker = new Tracker({
    event: "article.copyLink",
    action: "share",
    category: "social",
});

const LoadingCard = () => (
    <div>
        <Placeholder.Paragraph lines={10} />
    </div>
);

function BasicContent(): JSX.Element {
    const storyBodyRef = useRef<HTMLDivElement | null>(null);
    const fortressContext = useFortress();
    const { isAllowed, isBlocked, isLoading } =
        getFortressState(fortressContext);

    const {
        breakpoints: { largeDesktop },
    } = useScreen();
    // I think we should use ScreenContext here, which refers to the
    // breakpoints created by ScreenProvider. This way all the components
    // will be sharing the same breakpoints. useScreen will create another
    // set of breakpoints inside the component. Although the breakpoint values
    // will be the same currently, it is not necessary. Will put in another PR
    // to replace the useScreen
    const {
        breakpoints: { smallDesktop },
    } = useContext(ScreenContext);
    const [recircCardsExperimentEnabled, setRecircCardsExperimentEnabled] =
        useState(false);
    const [isRecircCardsExperimentReady, setIsRecircCardsExperimentReady] =
        useState(false);
    const { story } = useContext(CurrentStoryContext);

    const trackCopyLinkClickEvent = useCallback(() => {
        copyLinkTracker.trackEvent({ label: "floating_link" });
    }, []);

    const {
        brand,
        authors,
        body,
        assistance,
        disclaimer,
        footer,
        footnotes,
        magazineIssue,
        trashline,
        url,
        readingUrl,
        readingDuration,
        language = "en",
        translationLanguage,
    } = story;

    const numberOfParagraphs = getNumberOfParagraphs(body.content);
    const isEditorialArticle =
        story.opinionArticleType === OPINION_ARTICLE_TYPE.EDITORIAL_BOARD;
    const isOpinionArticle = isOpinion(story);
    const shareStickyClass = createClasses([
        styles.shareSmallDesktop,
        isAllowed && styles.stickys,
    ]);
    const hasWeekendId =
        IS_THE_WEEKEND_ENABLED && getWeekendCollectionId(story);
    const hasWeekendNicodeAndInlineAudioPlayer =
        IS_THE_WEEKEND_ENABLED &&
        story.nicodes?.includes(WEEKEND_NICODES.all) &&
        hasInlineAudioPlayer(body.content);
    const shouldRenderTTS = Boolean(
        !hasWeekendNicodeAndInlineAudioPlayer &&
            isAllowed &&
            readingUrl &&
            readingDuration
    );
    const statsig = useStatsigClient();
    const aiArticlesTakeaways = useLayer("ai-articles-takeaways");
    useEffect(() => {
        if (statsig.client.loadingStatus !== "Ready") {
            return;
        }
        setIsRecircCardsExperimentReady(true);
        startStatsigPerfLog("SS-aiArticlesTakeawaysLayerGet");
        const enabled = aiArticlesTakeaways.get("enabled", "false");
        endStatsigPerfLog("SS-aiArticlesTakeawaysLayerGet");

        setRecircCardsExperimentEnabled(enabled === "true");
    }, [statsig.client.loadingStatus, aiArticlesTakeaways]);

    const shouldShowAITakeaways = getShouldShowAITakeaways(story);

    const [saveArticleElement, setSaveArticleElement] =
        useState<Element | null>(null);
    const [listenAudioElement, setListenAudioElement] =
        useState<Element | null>(null);
    const [followAuthorElement, setFollowAuthorElement] =
        useState<Element | null>(null);
    const [giftArticleElement, setGiftArticleElement] =
        useState<Element | null>(null);
    useTippy({
        tipKey: TipKey.TIP_ZERO,
        variant: TipVariant.DIALOG,
    });
    useTippy({
        tipKey: TipKey.SAVE_ARTICLE,
        variant: TipVariant.POPOVER,
        refNode: saveArticleElement,
    });
    useTippy({
        tipKey: TipKey.LISTEN_AUDIO,
        variant: TipVariant.POPOVER,
        refNode: listenAudioElement,
    });
    useTippy({
        tipKey: TipKey.FOLLOW_AUTHOR,
        variant: TipVariant.POPOVER,
        refNode: followAuthorElement,
        additionalSelectorPath: "[data-component=button]",
    });
    useTippy({
        tipKey: TipKey.GIFT_ARTICLE,
        variant: TipVariant.POPOVER,
        refNode: giftArticleElement,
        additionalSelectorPath: "[data-component=outlined-button]",
        fixed: true,
    });

    const aiTakeawaysElement = useMemo(() => {
        if (!shouldShowAITakeaways) {
            return null;
        }

        if (!isRecircCardsExperimentReady) {
            return <LoadingCard />;
        }

        if (recircCardsExperimentEnabled) {
            return <Takeaways story={story} />;
        }

        return <SummaryOnlyTakeaways story={story} />;
    }, [
        recircCardsExperimentEnabled,
        story,
        shouldShowAITakeaways,
        isRecircCardsExperimentReady,
    ]);
    return (
        <GridCenterContent>
            <div className="flex">
                <Share
                    vertical
                    fixed
                    className={shareStickyClass}
                    giftArticleRef={setGiftArticleElement}
                />
                <div className={styles.content}>
                    <div className={styles.bylineSpeech}>
                        {/* Opinion Article Byline and TTS are rendered in BasicHeader component */}
                        {!isOpinionArticle && (
                            <BasicByline
                                story={story}
                                className={styles.bylineSpeech}
                            />
                        )}
                        <div className={styles.bookmarkSpeech}>
                            <div ref={setSaveArticleElement}>
                                <Bookmarks
                                    apiUrl={NEXT_PUBLIC_REGISTRATION_URL}
                                    articleId={story.id}
                                    publishedAt={story.publishedAt}
                                    contentType={ContentType.Article}
                                    className={styles.bookmarkContainer}
                                />
                            </div>
                            <LanguagePreference
                                sourceLanguage={language}
                                targetLanguage={translationLanguage}
                                translationError={story.translationErrors}
                            />
                            {shouldRenderTTS && (
                                <div ref={setListenAudioElement}>
                                    <TextToSpeech />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.insertsContainer}>
                        <SubscriberLabel story={story} />
                        {magazineIssue && <MagazineInsert story={story} />}
                        {aiTakeawaysElement}
                    </div>
                    {translationLanguage && (
                        <TranslationDisclaimer
                            position="top"
                            sourceLanguage={language}
                            targetLanguage={translationLanguage}
                        />
                    )}

                    <StoryBody
                        body={body}
                        options={options}
                        isAllowed={isAllowed}
                        isLoading={isLoading}
                        isBlocked={isBlocked}
                        storyBodyRef={storyBodyRef}
                        numberOfParagraphs={numberOfParagraphs}
                    />
                    {isAllowed && (
                        <Footnotes>
                            <RichText
                                story={footnotes}
                                renderers={footnotesOptions}
                            />
                        </Footnotes>
                    )}
                    {isAllowed && (
                        <>
                            {assistance && (
                                <Assistance
                                    authors={assistance.map(({ name }) => name)}
                                    language={language}
                                />
                            )}
                            {disclaimer && (
                                <Disclaimer>{disclaimer}</Disclaimer>
                            )}
                            {trashline && <Trashline>{trashline}</Trashline>}
                            {translationLanguage && (
                                <TranslationDisclaimer
                                    position="bottom"
                                    sourceLanguage={language}
                                    targetLanguage={translationLanguage}
                                />
                            )}
                            {!smallDesktop && (
                                <BrandMetricsModule
                                    className={styles.brandMetricsContentBottom}
                                />
                            )}
                            {footer && isOpinion(story) && (
                                <Footer footer={footer} />
                            )}
                            <FloatingCopyLink
                                className={styles.floatingCopyLink}
                                url={url}
                                onCopy={trackCopyLinkClickEvent}
                            />
                            {hasWeekendId && (
                                <WeekendRedirectTout story={story} />
                            )}
                            {authors?.length && (
                                <div ref={setFollowAuthorElement}>
                                    <AuthorBioList
                                        brand={brand}
                                        isEditorialBoard={isEditorialArticle}
                                        editorialBoard={story.editorialBoard}
                                        authors={authors}
                                    />
                                </div>
                            )}
                            <Share
                                className={styles.shareBottom}
                                hideGiftArticleText
                            />
                            <Bookmarks
                                apiUrl={NEXT_PUBLIC_REGISTRATION_URL}
                                articleId={story.id}
                                publishedAt={story.publishedAt}
                                contentType={ContentType.Article}
                                large
                                className={styles.bookmarkBottom}
                            />
                            <div className={styles.shareFeedbackWrapper}>
                                <ContactUs />
                                <ShareFeedback
                                    feedbackUrl={SURVEY_FEEDBACK_URL}
                                    storySlug={story.slug}
                                />
                                {!isOpinionArticle && <TipTout />}
                                <TerminalTout centerAligned />
                            </div>
                        </>
                    )}
                    <UpNext />
                    {IS_BLENS_ENABLED && !largeDesktop && isAllowed && (
                        <BlensWithBrokerAd
                            wrap
                            className={styles.blens}
                            quoteIds={story.blensSecurities}
                            storyType={story.type}
                        />
                    )}
                    <Disqus />
                </div>
            </div>
        </GridCenterContent>
    );
}

export default BasicContent;
